import * as React from 'react';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useField, useForm, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import {
  ConfigSection,
  containerPath,
  FormTextField,
  layoutPath,
  RefPickerField,
} from '../../../features/stackBuilder';
import CustomVariablesFormFinal from '../../../components/CustomVariables/finalForm/CustomVariablesFormFinal';
import CustomPropertiesSetup from '../../../components/CustomProperties/CustomProperties/CustomPropertiesSetup';
import { TEMPLATE_DEFAULTS } from './sequenceModel';
import { SECTION_FIELDS } from './validate';

/* eslint-disable @typescript-eslint/no-explicit-any -- API entities */
const toNumber = (value: string) => (value === '' ? '' : Number(value));

/** True once a field in the section has an error the user has been shown (touched or a failed publish). */
function useSectionError(fields: string[]) {
  const { errors, touched, submitFailed } = useFormState({
    subscription: { errors: true, touched: true, submitFailed: true },
  });
  return fields.some((field) => errors?.[field] && (submitFailed || touched?.[field]));
}

function General({
  sites,
  sitesLoading,
  isEditMode,
}: {
  sites: any[] | undefined;
  sitesLoading: boolean;
  isEditMode: boolean;
}) {
  const { input: targeting } = useField<boolean>('targetReturningUsers', {
    type: 'checkbox',
    subscription: { value: true },
  });
  const { values } = useFormState({ subscription: { values: true } });
  const hasError = useSectionError(SECTION_FIELDS.general);
  const summary = [
    values.site?.domain,
    `flex ${values.flex ?? '–'}`,
    `priority ${values.priority ?? '–'}`,
    values.targetReturningUsers ? `${values.returnVisits ?? 0} return visits` : null,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <ConfigSection title='General' summary={hasError ? 'Needs attention' : summary} hasError={hasError} defaultExpanded>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' } }}>
        <RefPickerField
          name='site'
          label='Site'
          textField='domain'
          options={sites}
          loading={sitesLoading}
          disabled={isEditMode}
          helperText={isEditMode ? 'Set when the sequence is created.' : undefined}
        />
        <FormTextField name='flex' label='Flex' type='number' parse={toNumber} />
        <FormTextField name='priority' label='Priority' type='number' parse={toNumber} />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          alignItems: 'center',
          mt: 1.5,
        }}
      >
        <FormControlLabel
          control={<Switch checked={Boolean(targeting.checked)} onChange={targeting.onChange} />}
          label='Target returning users'
        />
        {targeting.checked ? (
          <FormTextField name='returnVisits' label='Return visits' type='number' parse={toNumber} />
        ) : (
          <span />
        )}
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <FormTextField
          name='sequenceDescription'
          label='Description'
          multiline
          minRows={2}
          placeholder='What this sequence is for'
        />
      </Box>
    </ConfigSection>
  );
}

function Templates({
  layouts,
  containers,
  offerTypesByName,
  loading,
}: {
  layouts: any[] | undefined;
  containers: any[] | undefined;
  offerTypesByName: Record<string, any> | undefined;
  loading: boolean;
}) {
  const { values } = useFormState({ subscription: { values: true } });
  const hasError = useSectionError(SECTION_FIELDS.templates);
  const set = TEMPLATE_DEFAULTS.reduce(
    (count, d) => count + (values[d.layoutField] ? 1 : 0) + (values[d.containerField] ? 1 : 0),
    0
  );
  return (
    <ConfigSection
      title='Default layouts and containers'
      summary={hasError ? 'Needs attention' : `${set} of ${TEMPLATE_DEFAULTS.length * 2} set`}
      hasError={hasError}
      defaultExpanded={set < TEMPLATE_DEFAULTS.length * 2}
    >
      <Typography variant='caption' color='text.secondary' component='p' sx={{ mt: 0, mb: 1.5 }}>
        What each offer type renders with unless the offer overrides it. Open an offer in the stack to override.
      </Typography>
      <Box
        role='table'
        aria-label='Default layouts and containers by offer type'
        sx={{ display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr) minmax(0, 1fr)', gap: 1, alignItems: 'start' }}
      >
        <span role='columnheader' />
        <Typography role='columnheader' variant='caption' color='text.secondary' sx={{ fontWeight: 500 }}>
          Layout
        </Typography>
        <Typography role='columnheader' variant='caption' color='text.secondary' sx={{ fontWeight: 500 }}>
          Container
        </Typography>
        {TEMPLATE_DEFAULTS.map(({ offerType, layoutField, containerField }) => {
          const typeId = offerTypesByName?.[offerType]?.id;
          const typeContainers = containers?.filter(
            (container) => typeId != null && container.offerTypeHint === typeId
          );
          return (
            <React.Fragment key={offerType}>
              <Typography role='rowheader' variant='body2' sx={{ fontWeight: 500, pt: 1 }}>
                {offerType}
              </Typography>
              <RefPickerField
                name={layoutField}
                label={`${offerType} layout`}
                hideLabel
                options={layouts}
                loading={loading}
                linkTo={(layout) => layoutPath(layout.id)}
              />
              <RefPickerField
                name={containerField}
                label={`${offerType} container`}
                hideLabel
                options={typeContainers}
                loading={loading}
                linkTo={(container) => containerPath(container.id)}
              />
            </React.Fragment>
          );
        })}
      </Box>
    </ConfigSection>
  );
}

function CustomVariables() {
  const { values } = useFormState({ subscription: { values: true } });
  const hasError = useSectionError(SECTION_FIELDS.customVariables);
  const count = Array.isArray(values.customVariables) ? values.customVariables.length : 0;
  return (
    <ConfigSection
      title='Custom variables'
      summary={hasError ? 'Needs attention' : count === 1 ? '1 variable' : `${count} variables`}
      hasError={hasError}
    >
      <FieldArray name='customVariables' component={CustomVariablesFormFinal as any} customVarFlag='SUD_' />
    </ConfigSection>
  );
}

function CustomProperties() {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const ids: number[] = values.propertySetsIds ?? [];
  return (
    <ConfigSection
      title='Custom properties'
      summary={ids.length === 1 ? '1 property set' : `${ids.length} property sets`}
    >
      <CustomPropertiesSetup
        type='modal'
        propertySetsIds={ids}
        propertySetsValues={values.propertySetsValues ?? {}}
        setPropertySetsIds={(next: number[]) => form.change('propertySetsIds', next)}
        setPropertySetsValues={(next: Record<string, unknown>) => form.change('propertySetsValues', next)}
      />
    </ConfigSection>
  );
}

export default function SequenceSettings(props: {
  isEditMode: boolean;
  sites: any[] | undefined;
  sitesLoading: boolean;
  layouts: any[] | undefined;
  containers: any[] | undefined;
  offerTypesByName: Record<string, any> | undefined;
  templatesLoading: boolean;
}) {
  return (
    <>
      <General sites={props.sites} sitesLoading={props.sitesLoading} isEditMode={props.isEditMode} />
      <Templates
        layouts={props.layouts}
        containers={props.containers}
        offerTypesByName={props.offerTypesByName}
        loading={props.templatesLoading}
      />
      <CustomVariables />
      <CustomProperties />
    </>
  );
}
